import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import GradientMesh from "@/components/GradientMesh";
import ComingSoonBadge from "@/components/ComingSoonBadge";
import { COMING_SOON_BY_PLAN } from "@/lib/comingSoon";
import {
  Sparkles,
  Wand2,
  CalendarDays,
  Plane,
  Check,
  ArrowRight,
  Gem,
  Share2,
  Images,
  BookOpen,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Marketing page: /ai-social-media-management
// ---------------------------------------------------------------------------
// SEO pillar page targeting the query "ai social media management".
// All factual claims are traceable to code in server.py and Landing.js
// per the Batch 1 verified feature matrix. This file MUST NOT be edited
// without re-reading the verified matrix in the plan-of-record — the
// unit tests in src/__tests__/marketing_batch1.test.js lock the critical
// sentences so accidental copy drift breaks the build.
// ---------------------------------------------------------------------------

const NETWORK_ROWS = [
  {
    name: "Facebook",
    body:
      "Connect your Facebook Pages and Postmoro will list every Page you manage so you can pick which one to post from. Publish directly to Facebook Pages from Postmoro — draft creation, calendar planning, Brand Learning, and live publishing all work for your Page today.",
  },
  {
    name: "Instagram",
    body:
      "Connect an Instagram professional account (Business or Creator) linked to your Facebook Page. Postmoro drafts single-image feed posts — the caption, the hashtags, and the image, ready to review — and publishes them live to Instagram. Reels and Stories aren't part of the composer today.",
  },
  {
    name: "LinkedIn",
    body:
      "Postmoro posts to your LinkedIn personal profile. Publishing is live today. LinkedIn may periodically require you to reconnect your account. Postmoro shows an advance warning when your LinkedIn connection is nearing expiration and provides a direct reconnect option. Company Page posting isn't part of Postmoro yet.",
  },
  {
    name: "X",
    body:
      "Postmoro publishes single posts on X — text on its own, or text with one image. Publishing is live today. Threads, video, and long-form aren't part of the composer yet.",
  },
];

const PLANS = [
  {
    id: "free",
    name: "Free Trial",
    price: "$0",
    period: "month",
    features: [
      "10 AI generations / month",
      "5 published posts / month",
      "All 4 social platforms",
      "No credit card required",
    ],
  },
  {
    id: "starter",
    name: "Starter",
    price: "$19.99",
    period: "month",
    features: [
      "50 AI generations / month",
      "25 published posts / month",
      "All 4 social platforms",
      "Content calendar",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    highlight: true,
    price: "$79.99",
    period: "month",
    features: [
      "200 AI generations / month",
      "60 published posts / month",
      "All social platforms",
      "Marketing campaign ideas",
      "Drag-and-drop calendar",
      "Priority email + chat support",
    ],
  },
  {
    id: "business",
    name: "Business",
    price: "$249.99",
    period: "month",
    features: [
      "1,000 AI generations / month",
      "Unlimited published posts (fair-use)",
      "Teams — 3 team seats included",
      "Multi-platform Autopilot batching",
      "Business Media Library (30 reusable images)",
      "Custom brand voice",
      "Priority support + onboarding",
    ],
  },
];

const FAQS = [
  {
    q: "Does Postmoro post for me automatically, or do I approve first?",
    a: "By default, every Autopilot draft lands in your approval queue and only publishes when you approve it. If you'd prefer fully hands-off, there's a switch in Autopilot settings that lets Postmoro publish on the schedule you set without asking each time. You choose the mode; you can change it whenever.",
  },
  {
    q: "Which social networks can I connect?",
    a: "Facebook, Instagram, LinkedIn, and X. Postmoro publishes directly to Facebook Pages and Instagram professional accounts, along with LinkedIn personal profiles and X — all live today.",
  },
  {
    q: "What happens if my LinkedIn access expires?",
    a: "LinkedIn may periodically require you to reconnect your account. Postmoro shows an advance warning when your LinkedIn connection is nearing expiration and provides a direct reconnect option.",
  },
  {
    q: "Do I need to bring my own hashtags?",
    a: "No — Postmoro's AI generates hashtags tied to the caption and image for each post. You can edit them, remove any you don't want, or add your own.",
  },
  {
    q: "How is Postmoro different from a generic scheduler?",
    a: "A generic scheduler is a text box and a clock. Postmoro drafts the post for you, keeps your brand voice through Brand Learning, and lines everything up on the same calendar you'd already be using. You spend the review time you would have spent anyway — not the drafting time.",
  },
];

function MarketingHeader() {
  return (
    <header
      className="sticky top-0 z-30 backdrop-blur bg-[color:rgba(11,13,16,0.72)] border-b border-[color:var(--border-subtle)]"
      data-testid="marketing-header"
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between gap-3">
        <Link
          to="/"
          className="flex items-center gap-2 min-w-0"
          data-testid="marketing-logo"
        >
          <div className="h-7 w-7 rounded-md bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)] flex items-center justify-center shadow-[0_4px_18px_rgba(27,168,159,0.45)] shrink-0">
            <Gem className="h-4 w-4" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight truncate">
            Postmoro
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm text-[color:var(--text-2)]">
          <Link
            to="/social-media-scheduler"
            className="hover:text-[color:var(--text-1)]"
            data-testid="marketing-nav-scheduler"
          >
            Scheduler
          </Link>
          <Link
            to="/"
            className="hover:text-[color:var(--text-1)]"
            data-testid="marketing-nav-home"
          >
            Home
          </Link>
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          <Link to="/login">
            <Button
              variant="ghost"
              className="text-[color:var(--text-2)] hover:text-[color:var(--text-1)] hidden sm:inline-flex"
              data-testid="marketing-signin"
            >
              Sign in
            </Button>
          </Link>
          <Link to="/login">
            <Button
              className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
              data-testid="marketing-start-free-header"
            >
              Start free
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function MarketingFooter() {
  return (
    <footer
      className="mt-24 border-t border-[color:var(--border-subtle)] py-10 text-sm text-[color:var(--text-3)]"
      data-testid="marketing-footer"
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 rounded bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)] flex items-center justify-center">
            <Gem className="h-3 w-3" />
          </div>
          <span>© {new Date().getFullYear()} Postmoro LLC</span>
        </div>
        <div className="flex items-center gap-5">
          <Link to="/privacy" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-privacy">
            Privacy
          </Link>
          <Link to="/terms" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-terms">
            Terms
          </Link>
          <Link to="/data-deletion" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-data-deletion">
            Data deletion
          </Link>
        </div>
      </div>
    </footer>
  );
}

export default function AiSocialMediaManagementPage() {
  return (
    <>
      <SEO
        title="AI Social Media Management for Small Businesses | Postmoro"
        description="Postmoro drafts your captions, hashtags, and images with AI, then schedules them across Facebook, Instagram, LinkedIn, and X. No credit card required."
        path="/ai-social-media-management"
      />
      <div
        className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)]"
        data-testid="ai-social-media-management-page"
      >
        <MarketingHeader />

        {/* Hero */}
        <section className="relative overflow-hidden">
          <GradientMesh />
          <div className="absolute inset-0 hero-glow-strong pointer-events-none" />
          <div className="noise-overlay" aria-hidden="true" />
          <div className="relative max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 sm:pt-20 lg:pt-24 pb-10 sm:pb-14">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
              <div
                className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-xs text-[color:var(--text-2)]"
                data-testid="hero-eyebrow"
              >
                <Sparkles className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
                AI social media management for small businesses
              </div>
              <h1
                className="font-display mt-5 text-[34px] sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05]"
                data-testid="hero-title"
              >
                AI social media management,{" "}
                <span className="gradient-text">on your terms</span>
              </h1>
              <p
                className="mt-5 text-base sm:text-lg text-[color:var(--text-2)] max-w-2xl"
                data-testid="hero-subtitle"
              >
                Postmoro drafts your captions, hashtags, and images with AI —
                then lines them up on one calendar you actually enjoy opening.
                Built for solo founders and small teams who want a real assistant,
                not a black box.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to="/login">
                  <Button
                    size="lg"
                    className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold shadow-[0_18px_50px_-12px_rgba(27,168,159,0.55)]"
                    data-testid="hero-cta-primary"
                  >
                    Start free
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
                <Link
                  to="/social-media-scheduler"
                  className="text-sm text-[color:var(--text-2)] hover:text-[color:var(--text-1)] inline-flex items-center gap-1"
                  data-testid="hero-cta-secondary"
                >
                  See the scheduler →
                </Link>
              </div>
              <ul
                className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-xs text-[color:var(--text-3)] max-w-xl"
                data-testid="hero-checks"
              >
                <li className="inline-flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5 text-[color:var(--success)] shrink-0" />
                  Free Trial plan, no credit card required
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5 text-[color:var(--success)] shrink-0" />
                  Review and edit posts before publishing
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5 text-[color:var(--success)] shrink-0" />
                  Autopilot is optional — on or off, any time
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5 text-[color:var(--success)] shrink-0" />
                  Works with Facebook, Instagram, LinkedIn, and X
                </li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Small businesses want help, not a black box */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-18">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <p className="eyebrow text-[color:var(--brand-primary)]">
                Why Postmoro
              </p>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                Small businesses want help, not a black box
              </h2>
              <p className="mt-4 text-[color:var(--text-2)] leading-relaxed">
                Most social media tools ask you to hand over the keys and hope
                for the best. Postmoro takes a calmer approach: our AI drafts
                the post, you keep the pen. Captions, hashtags, and images are
                ready in seconds, and you decide what actually goes out. It&apos;s
                built for solo founders and small teams who care about how they
                show up online but don&apos;t want to sit inside a scheduling tool
                all week.
              </p>
              <p className="mt-4 text-[color:var(--text-2)] leading-relaxed">
                Postmoro connects to Facebook, Instagram, LinkedIn, and X. It
                plans the week, drafts the posts, and lines them up on{" "}
                <Link
                  to="/social-media-scheduler"
                  className="underline underline-offset-2 hover:text-[color:var(--text-1)]"
                  data-testid="body-link-scheduler"
                >
                  one calendar for four networks
                </Link>
                . You spend the time that matters on your business — not on
                staring at an empty text box.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div
                className="relative rounded-2xl overflow-hidden border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] shadow-[0_25px_60px_-20px_rgba(0,0,0,0.6)]"
                data-testid="hero-product-image"
              >
                <img
                  src="/marketing/generate.png"
                  alt="Screenshot of the Postmoro Generate Posts page"
                  className="w-full h-auto object-cover object-left-top block"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* What the AI actually does */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <p className="eyebrow text-[color:var(--brand-primary)]">The AI</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            What the AI actually does
          </h2>
          <p className="mt-4 text-[color:var(--text-2)] leading-relaxed max-w-3xl">
            Postmoro&apos;s AI is designed to help small businesses create clearer,
            more relevant social media content. Three things happen every time
            you make a post.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <FeatureCard
              icon={Wand2}
              title="Captions in your voice"
              body="Postmoro drafts each caption based on your business profile, your tone, and the past posts you feed into Brand Learning. Every draft is editable — nothing is locked. Change a word, change the whole thing, or start over. The AI takes the first swing so you don't have to face a blank page."
            />
            <FeatureCard
              icon={Share2}
              title="Hashtags that match the post"
              body="Instead of pasting the same twenty tags on everything, Postmoro suggests hashtags that relate to what's actually in the caption and the image. You can edit or remove any of them before the post goes live."
            />
            <FeatureCard
              icon={Images}
              title="On-brand images"
              body="Postmoro generates an image for each post so you're not stuck picking through stock photography. If you'd rather use your own product, staff, or storefront photos, upload them once to the Business Media Library and they'll be available to reuse whenever you want."
            />
          </div>
        </section>

        {/* Autopilot vs one-off */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-6">
              <p className="eyebrow text-[color:var(--brand-primary)]">
                Two flows
              </p>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                Autopilot for the week, manual for the moment
              </h2>
              <p className="mt-4 text-[color:var(--text-2)] leading-relaxed">
                There are two ways to make posts in Postmoro, and they work
                together.
              </p>
            </div>
            <div className="lg:col-span-6 space-y-6">
              <ModeCard
                icon={Plane}
                heading="Autopilot"
                body={
                  <>
                    Autopilot plans a whole week at once. Pick the days you want
                    to post, pick roughly what times work, and Postmoro fills
                    the calendar with drafts. By default, each draft lands in
                    an approval queue so you can look at it, edit anything, and
                    send it live yourself. When you&apos;re ready to hand over more
                    of the driving, one toggle in Autopilot settings switches
                    to fully automatic — Postmoro then publishes on the
                    schedule you set without asking again.
                  </>
                }
              />
              <ModeCard
                icon={Wand2}
                heading="Manual"
                body={
                  <>
                    Manual is for the one-off. Something happened today that
                    you want a post about. Open Generate, type the topic, get a
                    caption and image in a few seconds, edit to taste, schedule
                    or publish. You can flip between Manual and Autopilot
                    whenever you like — there&apos;s no separate workflow to learn.
                  </>
                }
              />
            </div>
          </div>
        </section>

        {/* Networks */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <p className="eyebrow text-[color:var(--brand-primary)]">Networks</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            The networks Postmoro publishes to
          </h2>
          <p className="mt-4 text-[color:var(--text-2)] leading-relaxed max-w-3xl">
            Postmoro supports four networks — Facebook, Instagram, LinkedIn,
            and X. That&apos;s the list; nothing else is quietly implied.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            {NETWORK_ROWS.map((n) => (
              <div
                key={n.name}
                className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-6"
                data-testid={`network-card-${n.name.toLowerCase()}`}
              >
                <h3 className="font-display text-lg font-semibold">{n.name}</h3>
                <p className="mt-3 text-sm text-[color:var(--text-2)] leading-relaxed">
                  {n.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Plans */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <p className="eyebrow text-[color:var(--brand-primary)]">Plans</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            Plans that grow when you do
          </h2>
          <p className="mt-4 text-[color:var(--text-2)] leading-relaxed max-w-3xl">
            Postmoro has four plans, and every plan has real limits so you
            always know what you&apos;re getting. Start on Free Trial and move up
            whenever your posting volume grows.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PLANS.map((p) => (
              <div
                key={p.id}
                className={`rounded-2xl border p-5 ${
                  p.highlight
                    ? "border-[color:rgba(27,168,159,0.55)] bg-[color:var(--surface-1)] glow-primary"
                    : "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]"
                }`}
                data-testid={`plan-card-${p.id}`}
              >
                <div className="flex items-baseline justify-between">
                  <div className="font-display text-lg font-semibold">
                    {p.name}
                  </div>
                  {p.highlight && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] font-semibold">
                      Popular
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-display text-2xl font-semibold">
                    {p.price}
                  </span>
                  <span className="text-xs text-[color:var(--text-3)]">
                    / {p.period}
                  </span>
                </div>
                <ul className="mt-4 space-y-1.5 text-sm text-[color:var(--text-2)]">
                  {p.features.map((f) => (
                    <li key={f} className="inline-flex items-start gap-2">
                      <Check className="h-4 w-4 text-[color:var(--success)] shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                  {(COMING_SOON_BY_PLAN[p.id] || []).length > 0 && (
                    <>
                      {(COMING_SOON_BY_PLAN[p.id] || []).map((f, i) => (
                        <li key={f} className={"flex gap-2 items-start opacity-90" + (i === 0 ? " pt-3 mt-2 border-t border-[color:var(--border-subtle)]" : "")} data-testid={`plan-coming-soon-row-${p.id}`}>
                          <Sparkles className="h-4 w-4 text-[color:rgb(255,190,110)] shrink-0 mt-0.5" aria-hidden="true" />
                          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                            <span>{f.replace(/\s*—\s*Coming Soon\s*$/i, "")}</span>
                            <ComingSoonBadge />
                          </span>
                        </li>
                      ))}
                    </>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section
          className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14"
          data-testid="faq-section"
        >
          <p className="eyebrow text-[color:var(--brand-primary)]">FAQ</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            Frequently asked questions
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            {FAQS.map((item, idx) => (
              <div
                key={item.q}
                className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5"
                data-testid={`faq-item-${idx}`}
              >
                <div className="inline-flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-[color:var(--brand-primary)]" />
                  <h3 className="font-display text-base font-semibold">
                    {item.q}
                  </h3>
                </div>
                <p className="mt-3 text-sm text-[color:var(--text-2)] leading-relaxed">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-8 sm:p-10 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
              Ready to try Postmoro?
            </h2>
            <p className="mt-3 text-[color:var(--text-2)] max-w-xl mx-auto">
              Start free, connect a network, generate your first post. No credit
              card required.
            </p>
            <div className="mt-6">
              <Link to="/login">
                <Button
                  size="lg"
                  className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                  data-testid="closing-cta"
                >
                  Start free
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5">
      <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 font-display text-lg font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-[color:var(--text-2)] leading-relaxed">
        {body}
      </p>
    </div>
  );
}

function ModeCard({ icon: Icon, heading, body }) {
  return (
    <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5">
      <div className="inline-flex items-center gap-2">
        <div className="h-8 w-8 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]">
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="font-display text-base font-semibold">{heading}</h3>
      </div>
      <p className="mt-3 text-sm text-[color:var(--text-2)] leading-relaxed">
        {body}
      </p>
    </div>
  );
}
